import type { CasinoGame, Promo } from '@ps/web-casino-types';
import { canUseDOM } from '@ps/web-core-utils';
import type { CasinoGameSlider } from '@ps/web-martech-cms-types';
import { gameEntity, promoEntity } from '@ps/web-shared-analytics';
import { takeUntil, throttleTime } from 'rxjs/operators';

import type { CasinoGameGridWidgetModel } from 'widgets/casino-game-grid-widget/model';
import {
  UPDATE_THROTTLE_TIME,
  clearState,
  extractPromoStream,
  favorites,
  freeSpinBonus,
  getIcon,
  injectPromo,
  isFSB,
  isPromo,
  preload,
  setLoading,
  transformParams,
  userLoggedIn,
  userLoggedOut,
  userUpdates
} from '@swf/casino';
import type { UseWidget } from '@swf/core/hooks/useWidget';
import { useWidget } from '@swf/core/hooks/useWidget';
import { areGamesVisibleByConfig, initialState } from '@swf/core/models/casino/utils';
import type {
  ExtractDispatchFromModel,
  ExtractSelectFromModel,
  Slicer,
  WidgetEffectParams,
  WidgetModelConfig
} from '@swf/core/store';
import type { StreamData } from '@swf/core/types/CasinoGame';
import { currencyChange, logout } from '@swf/core/utils/actions';

export interface CasinoGameSliderWidgetState extends StreamData<CasinoGame | Promo> {
  loading: boolean;
}

const reducers = {
  set: (state: CasinoGameSliderWidgetState, payload: CasinoGameSliderWidgetState) => payload,
  setLoading,
  favorites,
  freeSpinBonus
};

const effects = {
  clearState,
  preload,
  userUpdates,
  userLoggedIn,
  userLoggedOut,
  async fetch(payload: undefined, params: WidgetEffectParams<CasinoGameSlider>) {
    const { actions, dispatch, select, request, requestStream, rootState, widget } = params;
    const { uid, params: body, game_service: service } = widget;
    const widgetDispatch: ExtractDispatchFromModel<CasinoGameSliderWidgetModel> = dispatch[uid];
    const widgetSelect: ExtractSelectFromModel<CasinoGameGridWidgetModel> = select[uid];
    const options = { service, body: transformParams(body) };
    const { locale, promotionStream } = extractPromoStream(widget, rootState);
    const renderIcon = widgetSelect.getIcon(rootState, params.widget);
    const onLogout = logout(actions) as NonNullable<ReturnType<typeof logout>>;

    if (
      !areGamesVisibleByConfig({
        params,
        service,
        hasState: widgetSelect.get(rootState)?.games?.length > 0
      })
    ) {
      return;
    }

    if (!canUseDOM) {
      const { body: data } = await request<StreamData<CasinoGame>>(options);
      widgetDispatch.set({
        ...injectPromo(data, 0, locale, () => renderIcon, promotionStream),
        loading: false
      });
    } else {
      requestStream<StreamData<CasinoGame>>(options, undefined)
        .pipe(
          throttleTime(UPDATE_THROTTLE_TIME, undefined, { leading: true, trailing: true }),
          takeUntil(currencyChange(actions) as typeof actions),
          takeUntil(onLogout)
        )
        .subscribe((update) =>
          widgetDispatch.set({
            ...injectPromo(update, 0, locale, () => renderIcon, promotionStream),
            loading: false
          })
        );
    }
  }
};

const selectors = (slice: Slicer<CasinoGameSliderWidgetState>) => ({
  get() {
    return slice;
  },
  getIcon,
  isFSB,
  getEntities() {
    return slice(({ games }) =>
      games.slice(0, 20).map((game) => (isPromo(game) ? promoEntity(game) : gameEntity(game)))
    );
  }
});

export interface CasinoGameSliderWidgetModel
  extends WidgetModelConfig<CasinoGameSlider, CasinoGameSliderWidgetState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

export const sliderWidgetModel: CasinoGameSliderWidgetModel = {
  state: initialState,
  reducers,
  effects,
  selectors
};

export const useSliderWidgetModel: UseWidget<CasinoGameSlider, CasinoGameSliderWidgetModel> =
  useWidget;
